/**
 * CORE-BN — Front-of-House Attention Capacity · data access
 *
 * The read model. Every query here is shop-scoped, every query is
 * independently fault-isolated, and every query returns either a COUNT or
 * a row shape with no person column on it.
 *
 * Fault isolation is a constitutional requirement, not defensive habit.
 * Responsibility 8 says the model must degrade to today's behaviour when
 * it has no basis, and CORE-BN's Hardening note is explicit that "the
 * model must fail to a STATED ABSENCE rather than to a default figure,
 * because a fabricated capacity number is worse than no capacity number."
 * A single failing query must therefore mark its claim `'unavailable'`
 * and lower confidence — never silently contribute zero demand, and never
 * take the whole estimate down.
 *
 * Counts use `head: true` so the rows never cross the network at all.
 * That is the cheapest possible way to honour CORE-BN-PRIV-001: the
 * client cannot mishandle personal operational data it was never sent.
 *
 * Not pure — this is the I/O boundary. The pure aggregation it feeds
 * lives in `observations.ts`, and the pure model in `model.ts`.
 */

import { fetchMembershipsActingAs } from '@/lib/roles/membershipsActingAs';
import { frontOfficeRowsAt, type FrontOfficeScheduleEntry } from '@/lib/staffSchedule/frontOfficeOptIn';
import { supabase } from '@/integrations/supabase/client';
import {
  buildFohAttentionObservations,
  countClockedInFohStaff,
  countRosterFohStaff,
  resolveStaffingObservation,
  resolveTimeClockStatus,
  FOH_ATTENTION_STAFF_ROLES,
  type FohClockEntryRowLite,
  type FohCustodyRowLite,
  type FohMembershipRowLite,
  type FohTicketRowLite,
} from './observations';
import {
  readFohAttentionPolicyBlob,
  resolveFohAttentionPolicy,
  type FohAttentionPolicy,
} from './policy';
import type { FohAttentionClaimKind, FohAttentionObservations } from './types';

/** Custody statuses that constitute front-of-house demand. */
const FOH_CUSTODY_STATUSES = [
  'pending_advisor_accept',
  'with_advisor',
  'pending_tech_accept',
] as const;

/**
 * How far back the ticket query reaches. Waiters already in the lobby may
 * have been due hours ago, so the window alone is too narrow; a full day
 * is unnecessary. Twelve hours comfortably spans a shop day's morning
 * without dragging yesterday's board into today's estimate.
 */
const TICKET_LOOKBACK_MS = 12 * 60 * 60 * 1000;

export interface FetchFohAttentionArgs {
  shopId: string;
  /** Window start, injected by the caller. Usually "now". */
  windowStartIso: string;
  windowEndIso: string;
}

export interface FetchFohAttentionResult {
  observations: FohAttentionObservations;
  policy: FohAttentionPolicy;
}

/**
 * Run one query, converting any failure — thrown, returned error, or
 * malformed payload — into `null`. The caller records the source id and
 * downgrades the affected claim, which is what makes a partial outage
 * produce a partial estimate rather than a wrong one.
 */
async function safely<T>(
  sourceId: string,
  unavailable: string[],
  run: () => Promise<T>,
): Promise<T | null> {
  try {
    return await run();
  } catch (err) {
    console.error(`[core-bn] source "${sourceId}" unavailable`, err);
    unavailable.push(sourceId);
    return null;
  }
}

/** Exact row count without transferring any rows. */
async function countRows(
  table: string,
  apply: (q: ReturnType<typeof buildCountQuery>) => ReturnType<typeof buildCountQuery>,
): Promise<number> {
  const query = apply(buildCountQuery(table));
  const { count, error } = await query;
  if (error) throw error;
  return typeof count === 'number' ? count : 0;
}

function buildCountQuery(table: string) {
  // The generated `Database` types are regenerated from the hosted schema
  // and do not narrow usefully across the dynamic table name used here.
  // The surrounding code treats every result as untrusted anyway.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (supabase as any).from(table).select('*', { count: 'exact', head: true });
}

/**
 * Gather everything the model needs for one shop and one window.
 *
 * Never throws. A total outage produces observations with no staffing
 * basis and every claim `'unavailable'`, which the model turns into
 * `'unknown'` — the constitutionally required degradation.
 */
export async function fetchFohAttentionObservations(
  args: FetchFohAttentionArgs,
): Promise<FetchFohAttentionResult> {
  const { shopId, windowStartIso, windowEndIso } = args;

  const startMs = Date.parse(windowStartIso);
  const endMs = Date.parse(windowEndIso);
  const windowMs =
    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
      ? endMs - startMs
      : 0;
  // Communication and decision volume are estimated from the immediately
  // preceding window of equal length. When the window itself is invalid
  // the model will return `'unknown'` regardless, so a degenerate
  // trailing range costs nothing.
  const trailingStartIso = Number.isFinite(startMs)
    ? new Date(startMs - windowMs).toISOString()
    : windowStartIso;
  const ticketFloorIso = Number.isFinite(startMs)
    ? new Date(startMs - TICKET_LOOKBACK_MS).toISOString()
    : windowStartIso;

  const unavailableSources: string[] = [];
  const unavailableClaimKinds: FohAttentionClaimKind[] = [];

  const [
    shopProfile,
    custodyRows,
    ticketRows,
    memberships,
    clockEntries,
    callCount,
    smsCount,
    decisionCount,
  ] = await Promise.all([
    safely('shop_profile', unavailableSources, async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('shop_profile')
        .select('shop_culture, time_clock_enabled, staff_schedules, time_zone')
        .eq('shop_id', shopId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as {
        shop_culture: unknown;
        time_clock_enabled: boolean | null;
        staff_schedules?: FrontOfficeScheduleEntry[] | null;
        time_zone?: string | null;
      } | null;
    }),

    safely('ticket_custody', unavailableSources, async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('ticket_custody')
        .select('custody_status, entered_at')
        .eq('shop_id', shopId)
        .in('custody_status', FOH_CUSTODY_STATUSES);
      if (error) throw error;
      return (data ?? []) as FohCustodyRowLite[];
    }),

    safely('tickets', unavailableSources, async () => {
      // Location fields, not the `vehicle_present` mirror: presence is
      // derived by `isVehiclePresent`, which that column's own module
      // declares to be the sole authority.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('tickets')
        .select(
          'type, status, start_time, picked_up_at, parking_spot_id, bay_id, timer_status, parking_lot',
        )
        .eq('shop_id', shopId)
        .is('deleted_at', null)
        .is('archived_at', null)
        .gte('start_time', ticketFloorIso)
        .lt('start_time', windowEndIso);
      if (error) throw error;
      return (data ?? []) as FohTicketRowLite[];
    }),

    safely('memberships', unavailableSources, async () => {
      // Multi-role: effective roles, so front-office staff stored under another scalar count.
      return (await fetchMembershipsActingAs(supabase, shopId, FOH_ATTENTION_STAFF_ROLES)) as FohMembershipRowLite[];
    }),

    safely('time_clock_entries', unavailableSources, async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('time_clock_entries')
        .select('user_id, clock_in_time, clock_out_time')
        .eq('shop_id', shopId)
        .lte('clock_in_time', windowStartIso)
        .or(`clock_out_time.is.null,clock_out_time.gt.${windowStartIso}`);
      if (error) throw error;
      return (data ?? []) as FohClockEntryRowLite[];
    }),

    safely('call_events', unavailableSources, () =>
      countRows('call_events', (q) =>
        q
          .eq('shop_id', shopId)
          .gte('started_at', trailingStartIso)
          .lt('started_at', windowStartIso),
      ),
    ),

    safely('outbound_sms_log', unavailableSources, () =>
      countRows('outbound_sms_log', (q) =>
        q
          .eq('shop_id', shopId)
          .gte('created_at', trailingStartIso)
          .lt('created_at', windowStartIso),
      ),
    ),

    safely('advisor_decision_events', unavailableSources, () =>
      countRows('advisor_decision_events', (q) =>
        q
          .eq('shop_id', shopId)
          .gte('created_at', trailingStartIso)
          .lt('created_at', windowStartIso),
      ),
    ),
  ]);

  if (custodyRows === null) {
    unavailableClaimKinds.push(
      'custody_pending_accept',
      'custody_with_advisor',
      'custody_pending_tech_accept',
    );
  }
  if (ticketRows === null) {
    unavailableClaimKinds.push('arrival_intake', 'waiter_present');
  }

  // Communication is the sum of two independent sources. If EITHER is
  // down the count is incomplete, and an incomplete count of demand is
  // an understatement of demand — report it unavailable rather than
  // quietly halve the claim.
  const communicationCount =
    callCount === null || smsCount === null ? null : callCount + smsCount;

  const policy = resolveFohAttentionPolicy(
    readFohAttentionPolicyBlob(shopProfile?.shop_culture ?? null),
  );

  // The time clock's answer is only authoritative when we actually got
  // one. `resolveTimeClockStatus` keeps "the shop has no clock" apart
  // from "we could not read the clock" — conflating them is how a
  // successfully-read zero would get overruled by the roster.
  const timeClockStatus = resolveTimeClockStatus({
    shopProfileRead: shopProfile !== null,
    timeClockEnabled: shopProfile?.time_clock_enabled === true,
    clockEntriesRead: clockEntries !== null,
    membershipsRead: memberships !== null,
  });

  // Core-100 direction 12: a Technician + Service Advisor is front office only inside their explicit
  // front-office hours; the rest of their day is technician capacity (never both).
  const fohMemberships = frontOfficeRowsAt(
    (memberships ?? []) as Array<FohMembershipRowLite & { legacy_role?: string }>,
    shopProfile?.staff_schedules ?? null,
    windowStartIso,
    shopProfile?.time_zone ?? null,
  );
  const rosterCount = countRosterFohStaff(fohMemberships);
  const clockedInCount = countClockedInFohStaff(
    fohMemberships,
    clockEntries ?? [],
    windowStartIso,
  );
  const staffing = resolveStaffingObservation({
    timeClockStatus,
    clockedInCount,
    rosterCount,
  });

  const observations = buildFohAttentionObservations({
    shopId,
    windowStartIso,
    windowEndIso,
    staffing,
    custodyRows: custodyRows ?? [],
    ticketRows: ticketRows ?? [],
    trailingCommunicationCount: communicationCount,
    trailingDecisionCount: decisionCount,
    unavailableSources,
    unavailableClaimKinds,
  });

  return { observations, policy };
}
